import React, { useState } from "react";
import jwt_decode from "jwt-decode";

import { loanDataBasedOnLoanId, patchSaveApplication } from "../../../services/network";
import { getCookie } from "../../../utils/Common";
import { Roles } from "../../../utils/enums";

interface ProcessorAssigned {
  name: string;
  assignedOn: string;
}

// Self-contained renderCustom cell (owns its own state/network calls, like
// TermSheetCell) since ListView doesn't pass any row-mutation callback into
// CustomTable today. "Assign to me" claims the loan for the current
// processor by reading the full loan record, merging in processorAssigned,
// and writing it back via the same read-merge-write pattern Header.tsx's
// escalateLoan uses against patchSaveApplication — that endpoint expects the
// full data object, not a partial patch.
const ProcessorAssignedCell = ({ rowData }: { rowData: any }) => {
  const [processorAssigned, setProcessorAssigned] = useState<ProcessorAssigned | null>(
    rowData?.processorAssigned ?? null,
  );
  const [isAssigning, setIsAssigning] = useState(false);

  const userPermissions = JSON.parse(getCookie("userPermissions") || "[]");
  const isLoanProcessor = userPermissions.includes(Roles.loanProcessor);

  const handleAssignToMe = async () => {
    setIsAssigning(true);
    try {
      const accessToken = getCookie("sizer_access_token") || "";
      const decodedData: any = jwt_decode(accessToken);
      const name =
        decodedData?.name || decodedData?.email || decodedData?.user_id || "Unknown User";

      const { data } = await loanDataBasedOnLoanId([rowData.loanId]);
      const fullLoanData = data?.data?.["0"]?.data;
      const updatedProcessorAssigned: ProcessorAssigned = {
        name,
        assignedOn: new Date().toISOString(),
      };

      await patchSaveApplication(
        rowData.loanId,
        { ...fullLoanData, processorAssigned: updatedProcessorAssigned },
        true,
      );
      setProcessorAssigned(updatedProcessorAssigned);
    } catch (err) {
      console.error("Something went wrong while assigning the processor. ERROR:", err);
    } finally {
      setIsAssigning(false);
    }
  };

  if (processorAssigned?.name) {
    return (
      <span
        className="text-truncate"
        title={processorAssigned.name}
        data-testid="processor-assigned-name"
      >
        {processorAssigned.name}
      </span>
    );
  }

  if (isLoanProcessor) {
    return (
      <button
        type="button"
        onClick={handleAssignToMe}
        disabled={isAssigning}
        data-testid="assign-to-me-button"
        style={{
          border: "none",
          background: "none",
          padding: 0,
          font: "inherit",
          textDecoration: "underline",
          cursor: isAssigning ? "default" : "pointer",
          color: "var(--Primary-PrimaryMain-700, #40128B)",
          opacity: isAssigning ? 0.6 : 1,
        }}
      >
        {isAssigning ? "Assigning..." : "Assign to me"}
      </button>
    );
  }

  return <span data-testid="processor-unassigned">Unassigned</span>;
};

export default ProcessorAssignedCell;
